<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch, useTemplateRef } from 'vue'
import { useLenisStore, useState } from '#imports'
import type { Flat } from '~/types/types'

const specialFlats: Record<number, Flat> = {
	'-1': { id: -1, administrative_number: 'Lokal komercyjny' } as Flat,
	'-2': { id: -2, administrative_number: 'Sala telewizyjna' } as Flat,
	'-3': { id: -3, administrative_number: 'Przestrzeń spotkań integracyjnych' } as Flat,
	'-4': { id: -4, administrative_number: 'Centrum medyczne' } as Flat,
	'-5': { id: -5, administrative_number: 'Restauracja & Bistro' } as Flat,
	'-6': { id: -6, administrative_number: 'Pomieszczenie administracyjne' } as Flat,
}

const Flats = useState<Record<number, Flat>>('Flats')
const hoveredApartment = ref<Flat | null>(null)

const x = ref(0)
const y = ref(0)
const scrolled = ref(0)
let targetX = 0
let targetY = 0
let animationRaf: number | null = null
let mouseRaf: number | null = null

const visible = ref(false)
const selfElement = useTemplateRef('GhostHoverPanel')

const animate = () => {
  x.value += (targetX - x.value) * 0.15
  y.value += (targetY - y.value + scrolled.value) * 0.15
  if (Math.abs(targetX - x.value) > 0.5 || Math.abs(targetY - y.value) > 0.5) {
    animationRaf = requestAnimationFrame(animate)
  } else {
    animationRaf = null
  }
}

const updateMouse = (e: MouseEvent): void => {
  const el = (e.target as Element).closest('.ghost-element')
  const panelRect = selfElement.value?.getBoundingClientRect()
  if (!panelRect) return

  // lewa krawędź bloku zawierającego we współrzędnych viewportu
  const offsetParent = selfElement.value?.offsetParent as HTMLElement | null
  const originLeft = offsetParent ? offsetParent.getBoundingClientRect().left : 0

  if (el) {
    const rect = el.getBoundingClientRect()
    targetX = rect.left + rect.width / 2 - panelRect.width / 2 - originLeft
    targetY = Math.max(0, rect.top - panelRect.height)
    visible.value = true
    hoveredApartment.value = Number(el.id) < 10 ? specialFlats[Number(el.id)] as Flat : Flats.value[Number(el.id)] as Flat
  } else {
    targetX = e.clientX - panelRect.width / 2 - originLeft
    targetY = e.clientY - panelRect.height
    visible.value = false
  }

  if (!animationRaf) animate()
}
let lastMouseEvent: MouseEvent | null = null

const handleMouseMove = (e: MouseEvent) => {
  lastMouseEvent = e
  if (!mouseRaf) {
    mouseRaf = requestAnimationFrame(() => {
      if (lastMouseEvent) updateMouse(lastMouseEvent)
      mouseRaf = null
    })
  }
}

const handleScroll = (e: { velocity: number, actualScroll: number }) => {
  scrolled.value = e.actualScroll
  if (Math.abs(e.velocity) > 1) visible.value = false
  if (!animationRaf) animate()
}

const lenisStore = useLenisStore()

onMounted(() => {
  globalThis.addEventListener('mousemove', handleMouseMove)

  watch(
    () => lenisStore.lenis,
    (lenis) => {
      if (!lenis) return
      lenis.on('scroll', handleScroll)
    },
    { immediate: true }
  )
})

onUnmounted(() => {
  globalThis.removeEventListener('mousemove', handleMouseMove)

  if (animationRaf) cancelAnimationFrame(animationRaf)
  if (mouseRaf) cancelAnimationFrame(mouseRaf)

  if (lenisStore.lenis) {
    lenisStore.lenis.off('scroll', handleScroll)
  }
})

const currencyFormatter = new Intl.NumberFormat('pl-PL', {
  style: 'currency',
  currency: 'PLN'
})

const formatPrice = (value: number) => currencyFormatter.format(value)

const openPdf = () => {
  if (!hoveredApartment.value) return
  const url = Flats.value?.[hoveredApartment.value.id as number]?.card_link
  if (url) window.open(url)
}
</script>

<template>
  <div
    ref="GhostHoverPanel"
    class="z-1000 follower py-5 px-8 bg-background-1 absolute top-0 left-0 pointer-events-none origin-center flex flex-col xl:items-center gap-1 transition-opacity duration-200 rounded-3xl shadow-xl"
    :class="visible ? 'opacity-100' : 'opacity-0'"
    :style="{
      transform: `translate3d(${x}px, ${y}px, 0)`
    }"
  >
    <div>
      <span class="text-Paragraph text-Secondary">{{ hoveredApartment?.type == 'Komórka lokatorska' ? 'Komórka:' : 'Lokal:' }}&nbsp;</span>
      <span class="text-Paragraph font-bold text-Tekst">{{ hoveredApartment?.administrative_number ?? '' }}</span>
    </div>
    <div v-if="hoveredApartment?.area">
      <span class="text-Paragraph text-Secondary">Pow:&nbsp;</span>
      <span class="text-Paragraph font-bold text-Tekst">{{ hoveredApartment?.area ?? '' }} m<sup>2</sup></span>
    </div>
    <div v-if="hoveredApartment?.rooms">
      <span class="text-Paragraph text-Secondary">Pokoje: </span>
      <span class="text-Paragraph font-bold text-Tekst">{{ hoveredApartment.rooms }}</span>
    </div>
    <div v-if="hoveredApartment?.price">
      <span class="text-Paragraph text-Secondary">Cena:&nbsp;</span>
      <span class="text-Paragraph font-bold text-Tekst">{{ formatPrice(hoveredApartment.price) }}</span>
    </div>
    <div v-if="hoveredApartment?.pricemkw">
      <span class="text-Paragraph text-Secondary">Cena&nbsp;za&nbsp;m<sup>2</sup>:&nbsp;</span>
      <span class="text-Paragraph font-bold text-Tekst">{{ formatPrice(hoveredApartment.pricemkw) }}</span>
    </div>

    <div class="h-px bg-Tekst w-full" />
    <div>
      <span class="text-Paragraph font-bold text-Tekst">{{ hoveredApartment?.status_name ?? '' }}</span>
    </div>
    <div
      class="flex flex-col gap-1 justify-between pointer-events-auto"
      @click="openPdf"
      v-if="hoveredApartment?.plan_link"
    >
      <div class="px-6 py-2 flex flex-row gap-2 items-center justify-center">
        <span class="text-Paragraph font-bold text-Tekst">Kliknij by pobrać rzut</span>
        <NuxtImg
          src="https://api.ostoja-seniora.pl/files/plan-icon.svg"
          height="18"
          wifth="17"
          loading="lazy"
          alt="plan"
          class="object-contain w-4.5 h-4.5"
        />
      </div>
    </div>

    <svg
      width="17"
      height="15"
      viewBox="0 0 17 15"
      fill="none"
      class="absolute -bottom-3.5 self-center"
    >
      <path
        d="M9.20174 14.2035C8.81684 14.8701 7.85459 14.8701 7.46969 14.2035L0.135347 1.5C-0.249554 0.833333 0.231573 2.19098e-06 1.00137 2.25828e-06L15.6701 3.54066e-06C16.4399 3.60796e-06 16.921 0.833337 16.5361 1.5L9.20174 14.2035Z"
        fill="#F4F6F3"
      />
    </svg>
  </div>
</template>
